import styled from 'styled-components';
import { MainHead } from '../../../components/common/head/MainHead';
import { useLayout } from '../../../hooks/screen/useLayout';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { useGetUserSellTractors } from '../../../hooks/api/user/queries/useGetUserSellTractors';
import {
  IProductForSummaryCard,
  ProductSummaryCard,
} from '../../../views/Product/listView/ProductSummaryCard/ProductSummaryCard';
import { getOpenMarketProductTitle } from '../../../utils/getter/getOpenMarketProductTitle.util';
import {
  OpenMarketProductStatusConstant,
  OpenMarketProductStatusType,
  ProductStatusConstant,
  ProductStatusType,
} from '../../../constants/product/product.constant';
import { getOpenMarketProductPrice } from '../../../utils/getter/getProductPrice.util';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IOpenMarketProduct, IProductImage } from '../../../interfaces/Product.interfaces';
import { useAppDispatch } from '../../../hooks/store/useStore';
import {
  imageTypes,
  tractorImageObject,
  tractorImageType,
  userSellTractorSurveySlice,
} from '../../../store/userSellTractorSurveySlice';
import { usePatchUserSellTractorsMutation } from '../../../hooks/api/user/mutations/usePatchUserSellTractors.mutation';
import { useNotification } from '../../../provider/ToastNotificationProvider';
import { SellTractorButton } from '../../OpenMarket/OpenMarketListPage/components/SellTractorButton';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { getOpenMarketDetailPageURL } from '../../../utils/getter/getProductDetailPageURL.util';

export const MyInfoSellTractorListPage = () => {
  const [isShowEditModal, setIsShowEditModal] = useState(false);
  const [isMarkAsSoldoutModalVisible, setIsMarkAsSoldoutModalVisible] = useState(false);
  const [targetSellTractorId, setTargetSellTractorId] = useState<string>('');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userSellTractorActions = userSellTractorSurveySlice.actions;

  const { mutate: patchUserSellTractor, isLoading: isPatchUserSellTractorLoading } = usePatchUserSellTractorsMutation();

  const { openNotification } = useNotification();

  const {
    data: usersSellTractors,
    isLoading: isGetUsersSellTractorsLoading,
    refetch: refetchUsersSellTractors,
  } = useGetUserSellTractors();
  const { APPROVAL, ONGOING, REJECT } = OpenMarketProductStatusConstant;
  const { ONSALE, SOLDOUT } = ProductStatusConstant;

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true, directlyPassedURL: pageInfo.myInfoPage.path }}
        headerTitle={'판매 트랙터 관리'}
      />
    ),
    fixedHeaderHeight: 40,
    fixedFooter: <SellTractorButton pageType={'myinfo'} />,
    fixedFooterHeight: 0,
  });

  // TODO: 백-프론트 협의 후 이미지 데이터 형식 수정
  const mapImagesToTractorImageUrls = (images: IProductImage[]): tractorImageObject => {
    const imageUrls: tractorImageObject = {
      LEFT: '',
      FRONT: '',
      BACK: '',
      INNER: '',
      WORKINGMACHINE1: '',
      FRONTTIER: '',
      BACKTIER: '',
      ETC1: '',
      ETC2: '',
      ETC3: '',
    };

    images.forEach((image) => {
      const { type, imageUrl } = image;

      if (imageTypes.includes(type as tractorImageType)) {
        imageUrls[type as tractorImageType] = imageUrl;
      }
    });

    return imageUrls;
  };

  const handleEdit = (product: IOpenMarketProduct) => {
    dispatch(
      userSellTractorActions.changeSellTractorSurveyForEdit({
        sellTractorIdForEdit: product.id,
        brand: product.machine.brand.name,
        brandId: product.machine.brand.id,
        model: product.machine.model,
        engineOutputInPs: product.machine.engineOutputInPs,
        manufacturedYear: product.manufacturedYear,
        operationTime: product.operationTime,
        option: product.option,
        optionDetails: product.optionDetails,
        workingMachine: product.workingMachine,
        workingMachineDetails: product.workingMachineDetails,
        replacedParts: product.replacedParts,
        needRepairParts: product.needRepairParts,
        frontTireCondition: product.frontTireCondition,
        rearTireCondition: product.rearTireCondition,
        information: product.information,
        tractorImageUrls: mapImagesToTractorImageUrls(product.images),
        location: product.location,
        hopeSellingPrice: product.hopeSellingPrice / 10000,
        directImportYn: product.directImportYn,
        priceNegotiationYn: product.priceNegotiationYn,
      }),
    );
    setIsShowEditModal(true);
  };

  const closeMarkAsSoldoutModal = () => {
    setTargetSellTractorId('');
    setIsMarkAsSoldoutModalVisible(false);
  };

  const sellTractorStatusMappings = {
    markAsSoldout: {
      newStatus: SOLDOUT,
      newApprovalStatus: APPROVAL,
      successMessage: '판매 완료로 전환 되었어요',
      shouldCloseModal: true,
    },
    markAsOnsale: {
      newStatus: ONSALE,
      newApprovalStatus: ONGOING,
      successMessage: '심사후 판매중으로 전환 됩니다',
      shouldCloseModal: false,
    },
  };

  const updateSellTractorStatus = (id: string, statusMappingKey: 'markAsSoldout' | 'markAsOnsale') => {
    if (!id) {
      return;
    }
    const { newStatus, newApprovalStatus, successMessage, shouldCloseModal } =
      sellTractorStatusMappings[statusMappingKey];

    patchUserSellTractor(
      {
        sellTractorId: id,
        reqBody: {
          status: newStatus,
          approvalStatus: newApprovalStatus,
        },
      },
      {
        onSuccess: () => {
          if (shouldCloseModal) {
            closeMarkAsSoldoutModal();
          }
          refetchUsersSellTractors();
          openNotification({ message: successMessage });
        },
      },
    );
  };

  const handleMarkAsSoldout = (sellTractorId: string) => {
    updateSellTractorStatus(sellTractorId, 'markAsSoldout');
  };

  const handleMarkAsOnsale = (sellTractorId: string) => {
    updateSellTractorStatus(sellTractorId, 'markAsOnsale');
  };

  const getBlurEffect = (status: ProductStatusType, approvalStatus: OpenMarketProductStatusType) => {
    const BLURRED = true;
    const NOT_BLURRED = false;
    const DIMMED_TEXT = {
      SOLDOUT: '판매 완료',
      ONGOING: '심사중',
      REJECT: '판매불가',
      DEFAULT: '',
    };

    switch (approvalStatus) {
      case APPROVAL:
        return {
          isBlurred: status === SOLDOUT ? BLURRED : NOT_BLURRED,
          dimmedText: status === SOLDOUT ? DIMMED_TEXT.SOLDOUT : DIMMED_TEXT.DEFAULT,
        };

      case ONGOING:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.ONGOING,
        };

      case REJECT:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.REJECT,
        };

      default:
        return {
          isBlurred: NOT_BLURRED,
          dimmedText: DIMMED_TEXT.DEFAULT,
        };
    }
  };

  if (isGetUsersSellTractorsLoading) {
    return <AnimateLoading />;
  }

  if (usersSellTractors && usersSellTractors.length > 0) {
    return (
      <>
        <Container>
          <ProductSummaryCardGroup>
            {usersSellTractors.map((product: IOpenMarketProduct) => (
              <ProductSummaryCardWithActions>
                <ProductSummaryCard
                  handleClick={() => {
                    if (product.approvalStatus === APPROVAL && product.status === ONSALE) {
                      const url = getOpenMarketDetailPageURL(product.id);
                      navigate(url);
                    }
                  }}
                  key={product.id}
                  product={product as IProductForSummaryCard}
                  blurEffect={getBlurEffect(product.status, product.approvalStatus)}
                  price={getOpenMarketProductPrice(product.status, product.hopeSellingPrice)}
                  title={getOpenMarketProductTitle(product)}
                />
                {product.approvalStatus === APPROVAL ? (
                  <ButtonSection>
                    {product.status === ONSALE && (
                      <>
                        <GreyButton onClick={() => handleEdit(product)}>{'수정하기'}</GreyButton>
                        <GreyButton
                          onClick={() => {
                            setTargetSellTractorId(product.id);
                            setIsMarkAsSoldoutModalVisible(true);
                          }}
                        >
                          {'판매 완료로 전환'}
                        </GreyButton>
                      </>
                    )}
                    {product.status === SOLDOUT && (
                      <GreyButton
                        disabled={isPatchUserSellTractorLoading}
                        onClick={() => handleMarkAsOnsale(product.id)}
                      >
                        {'판매 중으로 전환'}
                      </GreyButton>
                    )}
                  </ButtonSection>
                ) : null}
              </ProductSummaryCardWithActions>
            ))}
          </ProductSummaryCardGroup>
        </Container>

        <PopupConfirmModal
          isShow={isShowEditModal}
          hasCloseButton={false}
          closeModal={() => setIsShowEditModal(false)}
          title={'매물 정보 수정시\n재심사가 필요해요! ⏳'}
          description={'건강한 매물 거래를 위해\n조금만 양해해 주시기 바랍니다.'}
          buttonArray={[
            {
              text: '확인',
              onClick: () => navigate('/survey/progress/sell-tractor/required-input-overview'),
              color: 'blue',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isMarkAsSoldoutModalVisible && !!targetSellTractorId}
          hasCloseButton={false}
          title={'알려드립니다 💡'}
          description={'판매중으로 다시 전환 하시려면\n재심사 과정이 필요해요!'}
          buttonArray={[
            {
              text: '판매 완료 하기',
              onClick: () => handleMarkAsSoldout(targetSellTractorId),
              color: 'blue',
            },
            {
              text: '닫기',
              onClick: () => {
                closeMarkAsSoldoutModal();
              },
              color: 'gray',
            },
          ]}
        />
      </>
    );
  }

  return <></>;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
`;

const ProductSummaryCardGroup = styled.div`
  display: flex;
  padding: var(--radius-large, 16px) var(--radius-large, 16px) 32px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const ProductSummaryCardWithActions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--radius-large, 16px);
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const GreyButton = styled.button`
  display: flex;
  max-height: 40px;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);
  flex: 1 0 0;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;
